import { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useParams, useLocation, useSearchParams, Link, Navigate } from "react-router-dom";
import axios from "axios";
import { MapPin, ArrowRight, ChevronLeft, ShoppingBag, ShieldCheck, Clock3, Plus, Minus, Check, LayoutDashboard, LogOut, X } from "lucide-react";
import "@/App.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `Rs. ${Number(n || 0).toLocaleString("en-PK")}`;
const resolveImage = (url) => url && url.startsWith("/api/files/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;
const api = axios.create({ baseURL: API, withCredentials: true });
const WhatsAppIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);
const fallbackImages = ["https://images.unsplash.com/photo-1568901346375-23c9450c58cd?auto=format&fit=crop&w=900&q=80"];
const PK_PHONE = /^(?:\+?92|0)?3\d{2}[-\s]?\d{7}$/;

function Brand({ light = false }) {
  return <Link data-testid="brand-link" className={`brand ${light ? "brand-light" : ""}`} to="/"><span className="brand-mark">F</span><span>FOODISTAN<small>Instant Food Deals</small></span></Link>;
}

function Shell({ children, locations, locationId, locationName, onLocationChange }) {
  return <>
    <header className="topbar">
      <Brand />
      <div className="location-pill" data-testid="current-location">
        <MapPin size={16} />
        <select aria-label="Selected location" value={locationId || ""} onChange={e => onLocationChange?.(e.target.value, locations.find(l => l.id === e.target.value)?.name)} data-testid="header-location-select">
          {!locationId && <option value="">{locationName || "Choose area"}</option>}
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
      </div>
      <Link className="admin-link" data-testid="restaurant-login-link" to="/restaurant">Partners</Link>
      <Link className="admin-link" data-testid="admin-login-link" to="/admin">Admin <LayoutDashboard size={16} /></Link>
    </header>
    {children}
  </>;
}

function Landing() {
  const nav = useNavigate();
  return <div className="landing">
    <div className="landing-photo" />
    <div className="landing-content">
      <Brand light />
      <span className="eyebrow">LOCAL FLAVOURS · BETTER PRICES</span>
      <h1>Good food.<br /><em>Better deals.</em></h1>
      <p>Discover hand-picked food offers around your neighbourhood, ready when you are.</p>
      <button className="primary-btn" data-testid="find-food-deals-button" onClick={() => nav("/locations")}>Find Food Deals <ArrowRight size={19} /></button>
      <div className="trust-row">
        <span><ShieldCheck size={17} /> Cash on delivery</span>
        <span><Clock3 size={17} /> Quick & easy</span>
      </div>
    </div>
  </div>;
}

function Locations() {
  const nav = useNavigate();
  const [locations, setLocations] = useState([]);
  useEffect(() => { api.get("/locations").then(r => setLocations(r.data)).catch(() => setLocations([])); }, []);
  const pick = (l) => {
    localStorage.setItem("foodistan_location", l.name);
    localStorage.setItem("foodistan_location_id", l.id);
    nav(`/deals?location=${encodeURIComponent(l.id)}`);
  };
  return <div className="page">
    <div className="narrow-head"><Brand /><span className="step-label">01 / 03</span></div>
    <main className="location-main">
      <span className="eyebrow">LET'S GET STARTED</span>
      <h1>Where are you<br /><em>located?</em></h1>
      <p className="muted">Pick your area to see the best deals available near you.</p>
      <div className="location-grid">
        {locations.map((l, i) => <button className="location-card" data-testid={`location-${l.name.toLowerCase().replaceAll(" ", "-")}`} key={l.id} onClick={() => pick(l)}>
          <span className="location-number">0{i + 1}</span>
          <strong>{l.name}</strong>
          <ArrowRight size={18} />
        </button>)}
      </div>
      <button className="manual-link" data-testid="manual-location-button" onClick={() => nav(`/deals`)}>Enter your location manually <ArrowRight size={15} /></button>
    </main>
  </div>;
}

function DealCard({ deal }) {
  const nav = useNavigate();
  return <article className="deal-card" data-testid={`deal-card-${deal.id}`}>
    <div className="deal-image-wrap">
      <img loading="lazy" src={resolveImage(deal.image_url) || fallbackImages[0]} alt={deal.title} />
      <span className="save-badge">Save {money(deal.savings)}</span>
    </div>
    <div className="deal-copy">
      <span className="restaurant-label">{deal.restaurant_name}</span>
      <h3>{deal.title}</h3>
      <p>{deal.description}</p>
      <div className="price-row">
        <strong>{money(deal.deal_price)}</strong>
        <s>{money(deal.original_price)}</s>
        <span className="delivery">+ {money(deal.delivery_fee)} delivery</span>
      </div>
      <button className="order-btn" data-testid={`order-now-${deal.id}`} onClick={() => nav(`/deal/${deal.id}`)}>Order Now <ArrowRight size={16} /></button>
    </div>
  </article>;
}

function Deals() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const locationId = params.get("location") || "";
  const [locations, setLocations] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restaurant, setRestaurant] = useState("all");
  const [max, setMax] = useState("all");

  useEffect(() => { api.get("/locations").then(r => setLocations(r.data)).catch(() => setLocations([])); }, []);
  useEffect(() => {
    setLoading(true);
    api.get(`/deals${locationId ? `?location_id=${locationId}` : ""}`).then(r => setDeals(r.data)).catch(() => setDeals([])).finally(() => setLoading(false));
  }, [locationId]);

  const locationName = locations.find(l => l.id === locationId)?.name || localStorage.getItem("foodistan_location") || "Your area";
  useEffect(() => { if (locationName && locationName !== "Your area") localStorage.setItem("foodistan_location", locationName); }, [locationName]);

  const changeLocation = (id, name) => {
    setRestaurant("all"); setMax("all");
    if (id) {
      if (name) localStorage.setItem("foodistan_location", name);
      localStorage.setItem("foodistan_location_id", id);
      setParams({ location: id });
    } else {
      setParams({});
    }
  };

  const restaurants = [...new Set(deals.map(d => d.restaurant_name))];
  const shown = useMemo(() => deals.filter(d => (restaurant === "all" || d.restaurant_name === restaurant) && (max === "all" || d.deal_price <= Number(max))), [deals, restaurant, max]);

  return <Shell locations={locations} locationId={locationId} locationName={locationName} onLocationChange={changeLocation}>
    <main className="deals-page">
      <section className="deals-hero">
        <div>
          <span className="eyebrow">TODAY'S PICKS · {locationName.toUpperCase()}</span>
          <h1>Food deals<br /><em>near you.</em></h1>
          <p>Fresh offers from local favourites, updated for your area.</p>
        </div>
        <div className="hero-plate" aria-hidden>🍔</div>
      </section>
      {!locationId && <div className="info-banner" data-testid="pick-location-banner">Pick an area above to see deals tailored to you — or <button className="link-btn" onClick={() => nav("/locations")}>choose a neighbourhood</button>.</div>}
      <div className="filter-bar">
        <span className="results-count" data-testid="results-count">{loading ? "Finding offers…" : `${shown.length} deal${shown.length === 1 ? "" : "s"} available`}</span>
        <select data-testid="restaurant-filter" value={restaurant} onChange={e => setRestaurant(e.target.value)}>
          <option value="all">All restaurants</option>
          {restaurants.map(r => <option key={r}>{r}</option>)}
        </select>
        <select data-testid="price-filter" value={max} onChange={e => setMax(e.target.value)}>
          <option value="all">Any price</option>
          <option value="800">Under Rs. 800</option>
          <option value="1200">Under Rs. 1,200</option>
          <option value="1500">Under Rs. 1,500</option>
        </select>
      </div>
      {loading ? <div className="deal-grid">{[0, 1, 2, 3].map(i => <div className="deal-card skeleton" key={i} />)}</div>
        : !shown.length ? <div className="empty-state" data-testid="empty-deals">No deals available in this location right now. Check back soon!</div>
          : <div className="deal-grid">{shown.map(d => <DealCard deal={d} key={d.id} />)}</div>}
    </main>
  </Shell>;
}

function Detail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [deal, setDeal] = useState(null);
  const [locations, setLocations] = useState([]);
  useEffect(() => { api.get(`/deals/${id}`).then(r => setDeal(r.data)).catch(() => setDeal(false)); api.get("/locations").then(r => setLocations(r.data)); }, [id]);
  if (deal === null) return <div className="loading">Loading your deal…</div>;
  if (!deal) return <div className="empty-state container">Sorry, this deal is no longer available.<Link to="/deals" className="link-btn">Back to deals</Link></div>;
  return <Shell locations={locations} locationId={deal.location_id} locationName={deal.location_name} onLocationChange={(id) => nav(`/deals?location=${id}`)}>
    <main className="detail-page">
      <button className="back-btn" data-testid="back-to-deals" onClick={() => nav(-1)}><ChevronLeft size={18} /> Back to deals</button>
      <div className="detail-layout">
        <img className="detail-image" src={resolveImage(deal.image_url)} alt={deal.title} />
        <div className="detail-copy">
          <span className="restaurant-label">{deal.restaurant_name} · {deal.location_name}</span>
          <h1>{deal.title}</h1>
          <p className="detail-description">{deal.description}</p>
          <div className="detail-price">
            <strong>{money(deal.deal_price)}</strong>
            <s>{money(deal.original_price)}</s>
            <span>Save {money(deal.savings)}</span>
          </div>
          <div className="info-line">
            <span><ShoppingBag size={18} /> Delivery {money(deal.delivery_fee)}</span>
            <span><Clock3 size={18} /> Available today</span>
          </div>
          <button className="primary-btn full-btn" data-testid="detail-order-now" onClick={() => nav(`/order/${deal.id}`)}>Order Now <ArrowRight size={19} /></button>
        </div>
      </div>
    </main>
  </Shell>;
}

function Order() {
  const { id } = useParams();
  const nav = useNavigate();
  const [deal, setDeal] = useState(null);
  const [locations, setLocations] = useState([]);
  const [form, setForm] = useState({ full_name: "", phone: "", whatsapp_number: "", email: "", address: "", quantity: 1, special_instructions: "", marketing_consent: false });
  const [sameAsPhone, setSameAsPhone] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get(`/deals/${id}`).then(r => setDeal(r.data)).catch(() => setDeal(false)); api.get("/locations").then(r => setLocations(r.data)); }, [id]);

  if (deal === null) return <div className="loading">Preparing checkout…</div>;
  if (!deal) return <div className="empty-state container">Sorry, this deal is no longer available.<Link to="/deals" className="link-btn">Back to deals</Link></div>;

  const quantity = Number(form.quantity) || 1;
  const subtotal = deal.deal_price * quantity;
  const total = subtotal + deal.delivery_fee;
  const update = e => setForm({ ...form, [e.target.name]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const setQty = (q) => setForm({ ...form, quantity: Math.min(20, Math.max(1, q)) });

  const submit = async e => {
    e.preventDefault();
    setError("");
    if (!form.full_name.trim() || form.full_name.trim().length < 2) return setError("Please enter your name.");
    const normalisedPhone = form.phone.replace(/\s/g, "");
    if (!PK_PHONE.test(normalisedPhone)) return setError("Please enter a valid mobile number (e.g. 03XX XXXXXXX).");
    if (!form.address.trim() || form.address.trim().length < 5) return setError("Please enter a complete delivery address.");
    const whatsappNumber = sameAsPhone ? normalisedPhone : form.whatsapp_number.replace(/\s/g, "");
    if (!sameAsPhone && !PK_PHONE.test(whatsappNumber)) return setError("Please enter a valid WhatsApp number (e.g. 03XX XXXXXXX).");
    setSaving(true);
    try {
      const payload = { ...form, phone: normalisedPhone, whatsapp_number: whatsappNumber, deal_id: id, quantity, location_id: deal.location_id };
      if (!payload.email) delete payload.email;
      const r = await api.post("/orders", payload);
      nav(`/confirmation/${r.data.order_number}`, { state: r.data });
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map(x => x.msg).join(", ") : (typeof d === "string" ? d : "We couldn't submit your order. Please try again.");
      setError(msg);
    } finally { setSaving(false); }
  };

  return <Shell locations={locations} locationId={deal.location_id} locationName={deal.location_name} onLocationChange={(id) => nav(`/deals?location=${id}`)}>
    <main className="checkout-page">
      <button className="back-btn" data-testid="checkout-back" onClick={() => nav(-1)}><ChevronLeft size={18} /> Back</button>
      <div className="checkout-grid">
        <form className="order-form" onSubmit={submit} noValidate>
          <span className="eyebrow">ALMOST THERE</span>
          <h1>Your order.</h1>
          <p className="muted">A few details and we'll get it moving.</p>
          <div className="form-section">
            <h2>Contact & delivery</h2>
            <label>Customer name<input data-testid="customer-name-input" name="full_name" required value={form.full_name} onChange={update} placeholder="Your full name" /></label>
            <label>Mobile number<input data-testid="phone-input" name="phone" required inputMode="tel" value={form.phone} onChange={update} placeholder="03XX XXXXXXX" /></label>
            <label className="consent"><input type="checkbox" checked={sameAsPhone} onChange={e => setSameAsPhone(e.target.checked)} data-testid="whatsapp-same-checkbox" /><span>My WhatsApp number is the same as my mobile number</span></label>
            {!sameAsPhone && <label>WhatsApp number<input data-testid="whatsapp-input" name="whatsapp_number" inputMode="tel" value={form.whatsapp_number} onChange={update} placeholder="03XX XXXXXXX" /></label>}
            <label>Email <small>optional</small><input data-testid="email-input" name="email" type="email" value={form.email} onChange={update} placeholder="you@example.com" /></label>
            <label>Delivery address<input data-testid="address-input" name="address" required value={form.address} onChange={update} placeholder="House, street, area" /></label>
            <label>Special instructions <small>optional</small><textarea data-testid="instructions-input" name="special_instructions" value={form.special_instructions} onChange={update} placeholder="Less spicy, no onions…" /></label>
            <label className="consent"><input data-testid="marketing-consent-checkbox" name="marketing_consent" type="checkbox" checked={form.marketing_consent} onChange={update} /><span>I agree to receive FOODISTAN offers and deals by SMS/WhatsApp/email.</span></label>
          </div>
          {error && <div className="error-message" data-testid="order-error">{error}</div>}
          <button className="primary-btn full-btn" data-testid="place-order-button" disabled={saving}>{saving ? "Sending order…" : "Place Order"} <ArrowRight size={19} /></button>
        </form>
        <aside className="summary-card">
          <span className="eyebrow">ORDER SUMMARY</span>
          <div className="summary-deal">
            <img src={resolveImage(deal.image_url)} alt="" />
            <div><strong>{deal.title}</strong><span>{deal.restaurant_name}</span></div>
          </div>
          <div className="quantity-row">
            <span>Quantity</span>
            <div>
              <button type="button" aria-label="Decrease" data-testid="quantity-decrease" onClick={() => setQty(quantity - 1)} disabled={quantity <= 1}><Minus size={16} /></button>
              <b data-testid="quantity-value">{quantity}</b>
              <button type="button" aria-label="Increase" data-testid="quantity-increase" onClick={() => setQty(quantity + 1)} disabled={quantity >= 20}><Plus size={16} /></button>
            </div>
          </div>
          <div className="totals">
            <span>Deal price <b>{money(deal.deal_price)}</b></span>
            <span>Subtotal <b data-testid="subtotal-value">{money(subtotal)}</b></span>
            <span>Delivery <b>{money(deal.delivery_fee)}</b></span>
            <span>Discount <b>Rs. 0</b></span>
            <strong>Total <b data-testid="total-value">{money(total)}</b></strong>
          </div>
          <div className="cod"><ShieldCheck size={18} /><span><b>Cash on Delivery</b><small>Pay when your order arrives</small></span></div>
        </aside>
      </div>
    </main>
  </Shell>;
}

function Confirmation() {
  const { state } = useLocation();
  const { id } = useParams();
  const data = state || {};
  if (!state) return <div className="empty-state container">We couldn't find that order confirmation.<Link to="/deals" className="link-btn">Back to deals</Link></div>;
  const summary = `FOODISTAN ORDER\nOrder: ${data.order_number || id}\nCustomer: ${data.full_name || ""}\nPhone: ${data.phone || ""}${data.whatsapp_number && data.whatsapp_number !== data.phone ? `\nWhatsApp: ${data.whatsapp_number}` : ""}\nRestaurant: ${data.restaurant_name || ""}\nDeal: ${data.deal_title || ""}\nQuantity: ${data.quantity || ""}\nTotal: ${money(data.total)}\nAddress: ${data.delivery_address || ""}${data.special_instructions ? `\nInstructions: ${data.special_instructions}` : ""}`;
  const targetNumber = (data.restaurant_whatsapp || data.business_whatsapp || "").replace(/[^0-9]/g, "");
  const whatsapp = targetNumber ? `https://wa.me/${targetNumber}?text=${encodeURIComponent(summary)}` : `https://wa.me/?text=${encodeURIComponent(summary)}`;
  const sendLabel = data.restaurant_whatsapp ? `Send Order to ${data.restaurant_name}` : (data.business_whatsapp ? "Send Order via WhatsApp" : "Share on WhatsApp");
  return <>
    <header className="topbar"><Brand /><span className="admin-chip">Order confirmed <Check size={14} /></span></header>
    <main className="confirmation">
      <div className="success-icon"><Check size={35} /></div>
      <span className="eyebrow">ORDER RECEIVED</span>
      <h1>Thanks, <em>we've got it.</em></h1>
      <p>We've received your order request. Our team will contact you to confirm your order.</p>
      <div className="confirmation-card">
        <span>Your order number</span>
        <strong data-testid="order-number">{data.order_number || id}</strong>
        <div><span>Deal</span><b>{data.deal_title}</b></div>
        <div><span>Restaurant</span><b>{data.restaurant_name}</b></div>
        <div><span>Quantity</span><b>{data.quantity}</b></div>
        <div><span>Total</span><b>{money(data.total)}</b></div>
        <div><span>Payment</span><b>Cash on Delivery</b></div>
      </div>
      <div className="confirmation-actions">
        <Link className="primary-btn" data-testid="back-to-deals-confirmation" to="/deals">Back to Deals <ArrowRight size={18} /></Link>
        <a className="secondary-btn whatsapp-btn" data-testid="whatsapp-order-button" href={whatsapp} target="_blank" rel="noreferrer"><WhatsAppIcon size={18} /> {sendLabel}</a>
      </div>
      {data.business_whatsapp && data.business_whatsapp.replace(/[^0-9]/g, "") && <a className="admin-alert-link" data-testid="admin-whatsapp-alert" href={`https://wa.me/${data.business_whatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(summary)}`} target="_blank" rel="noreferrer">Also notify FOODISTAN support →</a>}
    </main>
  </>;
}

function Admin() {
  const [auth, setAuth] = useState(null); // null = checking, false = login, true = in
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [adminDeals, setAdminDeals] = useState([]);
  const [adminLocations, setAdminLocations] = useState([]);
  const [adminRestaurants, setAdminRestaurants] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("ALL");
  const [modal, setModal] = useState(null); // { type, initial? } | null

  useEffect(() => {
    api.get("/auth/me").then(() => setAuth(true)).catch(() => setAuth(false));
  }, []);

  const reload = async () => {
    const [s, o, c, d, l, r] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/orders"), api.get("/admin/customers"),
      api.get("/admin/deals"), api.get("/admin/locations"), api.get("/admin/restaurants"),
    ]);
    setStats(s.data); setOrders(o.data); setCustomers(c.data);
    setAdminDeals(d.data); setAdminLocations(l.data); setAdminRestaurants(r.data);
  };

  useEffect(() => { if (auth === true) reload(); }, [auth]);

  const login = async e => {
    e.preventDefault();
    setError("");
    try { await api.post("/auth/login", { email, password }); setAuth(true); }
    catch (err) { setError(err.response?.data?.detail || "Invalid email or password"); }
  };

  const updateStatus = async (orderId, status) => {
    await api.patch(`/admin/orders/${orderId}`, { status });
    const r = await api.get("/admin/orders"); setOrders(r.data);
    const s = await api.get("/admin/stats"); setStats(s.data);
  };

  const deleteOrder = async (orderId, orderNumber) => {
    if (!window.confirm(`Delete order ${orderNumber}? This cannot be undone.`)) return;
    await api.delete(`/admin/orders/${orderId}`);
    const r = await api.get("/admin/orders"); setOrders(r.data);
    const s = await api.get("/admin/stats"); setStats(s.data);
  };

  const exportCSV = async () => {
    const r = await api.get("/admin/orders/export", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `foodistan-orders-${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  if (auth === null) return <div className="loading">Checking admin session…</div>;

  if (auth === false) return <div className="admin-login">
    <Brand />
    <form onSubmit={login}>
      <span className="eyebrow">PRIVATE ACCESS</span>
      <h1>Admin <em>dashboard.</em></h1>
      <p className="muted">Manage your deals and orders in one place.</p>
      <label>Email<input data-testid="admin-email-input" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@foodistan.pk" /></label>
      <label>Password<input data-testid="admin-password-input" type="password" required value={password} onChange={e => setPassword(e.target.value)} /></label>
      {error && <div className="error-message" data-testid="admin-login-error">{error}</div>}
      <button className="primary-btn full-btn" data-testid="admin-login-button">Sign in <ArrowRight size={18} /></button>
    </form>
    <Link to="/">← Back to FOODISTAN</Link>
  </div>;

  const filteredOrders = orders.filter(o => (filter === "ALL" || o.status === filter) && (!search || (o.order_number || "").toLowerCase().includes(search.toLowerCase()) || (o.customer_name || "").toLowerCase().includes(search.toLowerCase()) || (o.phone || "").includes(search)));

  return <div className="admin-layout">
    <aside className="admin-sidebar">
      <Brand />
      <p>OPERATIONS</p>
      {[["overview", "Overview"], ["orders", "Orders"], ["customers", "Customers"], ["deals", "Deals"], ["restaurants", "Restaurants"], ["locations", "Locations"]].map(([id, label]) =>
        <button className={tab === id ? "active" : ""} data-testid={`admin-tab-${id}`} key={id} onClick={() => setTab(id)}><LayoutDashboard size={17} />{label}</button>)}
      <button className="logout" data-testid="admin-logout-button" onClick={async () => { await api.post("/auth/logout"); setAuth(false); }}><LogOut size={17} />Sign out</button>
    </aside>
    <main className="admin-main">
      <div className="admin-top">
        <div>
          <span className="eyebrow">FOODISTAN CONTROL ROOM</span>
          <h1>{tab === "overview" ? "Dashboard" : tab[0].toUpperCase() + tab.slice(1)}</h1>
        </div>
        <span className="admin-chip">Admin <ShieldCheck size={15} /></span>
      </div>

      {tab === "overview" && <>
        <div className="stats-grid">
          {[["orders", "Total orders"], ["new_orders", "New orders"], ["delivered", "Delivered"], ["customers", "Customers"], ["deals", "Active deals"], ["restaurants", "Restaurants"], ["locations", "Locations"]].map(([k, l]) =>
            <div className="stat-card" data-testid={`stat-${k}`} key={k}><span>{l}</span><strong>{stats?.[k] ?? "—"}</strong></div>)}
        </div>
        <section className="admin-section">
          <div className="section-heading"><h2>Recent orders</h2><button data-testid="view-all-orders" onClick={() => setTab("orders")}>View all <ArrowRight size={16} /></button></div>
          <OrderTable orders={orders.slice(0, 5)} onUpdate={updateStatus} />
        </section>
      </>}

      {tab === "orders" && <section className="admin-section">
        <div className="toolbar">
          <input data-testid="order-search" placeholder="Search order #, name or phone" value={search} onChange={e => setSearch(e.target.value)} />
          <select data-testid="order-status-filter" value={filter} onChange={e => setFilter(e.target.value)}>
            {["ALL", "NEW", "CONFIRMED", "PREPARING", "OUT_FOR_DELIVERY", "DELIVERED", "CANCELLED"].map(s => <option key={s}>{s}</option>)}
          </select>
          <button className="primary-btn small-btn" data-testid="export-csv-button" onClick={exportCSV}>Download CSV</button>
        </div>
        <AdminOrderTable orders={filteredOrders} onUpdate={updateStatus} onDelete={deleteOrder} />
      </section>}

      {tab === "customers" && <section className="admin-section">
        <p className="muted">Customer records are kept private. Marketing is only sent to customers who gave consent.</p>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Phone</th><th>WhatsApp</th><th>Email</th><th>Address</th><th>Marketing</th></tr></thead>
            <tbody>{customers.map(c => <tr key={c.id} data-testid={`customer-row-${c.id}`}>
              <td>{c.full_name}</td><td>{c.phone}</td><td>{c.whatsapp_number || c.phone}</td><td>{c.email || "—"}</td><td>{c.delivery_address}</td>
              <td>{c.marketing_consent ? <span className="status status-delivered">Consented</span> : <span className="status status-cancelled">No</span>}</td>
            </tr>)}</tbody>
          </table>
          {!customers.length && <div className="empty-state">No customers yet.</div>}
        </div>
      </section>}

      {tab === "deals" && <section className="admin-section">
        <div className="section-heading"><h2>All deals</h2><button className="primary-btn small-btn" data-testid="add-deal-button" onClick={() => setModal({ type: "deal" })}><Plus size={16} /> Add deal</button></div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Title</th><th>Restaurant</th><th>Location</th><th>Deal price</th><th>Original</th><th>Delivery</th><th>Status</th><th></th></tr></thead>
            <tbody>{adminDeals.map(d => {
              const rest = adminRestaurants.find(r => r.id === d.restaurant_id);
              const loc = adminLocations.find(l => l.id === d.location_id);
              return <tr key={d.id}>
                <td>{d.title}</td><td>{rest?.name || "—"}</td><td>{loc?.name || "—"}</td>
                <td>{money(d.deal_price)}</td><td>{money(d.original_price)}</td><td>{money(d.delivery_fee)}</td>
                <td><button className={`status status-${d.active ? "delivered" : "cancelled"} status-toggle`} onClick={async () => { await api.patch(`/admin/deals/${d.id}`, { ...d, active: !d.active }); reload(); }}>{d.active ? "Active" : "Inactive"}</button></td>
                <td><button className="link-btn" data-testid={`edit-deal-${d.id}`} onClick={() => setModal({ type: "deal", initial: d })}>Edit</button></td>
              </tr>;
            })}</tbody>
          </table>
          {!adminDeals.length && <div className="empty-state">No deals yet. Click "Add deal" to create your first one.</div>}
        </div>
      </section>}

      {tab === "restaurants" && <section className="admin-section">
        <div className="section-heading"><h2>All restaurants</h2><button className="primary-btn small-btn" data-testid="add-restaurant-button" onClick={() => setModal({ type: "restaurant" })}><Plus size={16} /> Add restaurant</button></div>
        <p className="muted" style={{ marginBottom: 14 }}>Add a WhatsApp number for each restaurant — customer orders will open in WhatsApp directly to that number.</p>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Phone</th><th>WhatsApp</th><th>Address</th><th>Location</th><th>Status</th><th></th></tr></thead>
            <tbody>{adminRestaurants.map(r => {
              const loc = adminLocations.find(l => l.id === r.location_id);
              return <tr key={r.id} data-testid={`restaurant-row-${r.id}`}>
                <td>{r.name}</td><td>{r.phone || "—"}</td>
                <td>{r.whatsapp_number ? <span className="status status-delivered">{r.whatsapp_number}</span> : <span className="status status-cancelled">Not set</span>}</td>
                <td>{r.address || "—"}</td><td>{loc?.name || "—"}</td>
                <td><button className={`status status-${r.active ? "delivered" : "cancelled"} status-toggle`} onClick={async () => { await api.patch(`/admin/restaurants/${r.id}`, { ...r, active: !r.active }); reload(); }}>{r.active ? "Active" : "Inactive"}</button></td>
                <td><button className="link-btn" data-testid={`edit-restaurant-${r.id}`} onClick={() => setModal({ type: "restaurant", initial: r })}>Edit</button></td>
              </tr>;
            })}</tbody>
          </table>
          {!adminRestaurants.length && <div className="empty-state">No restaurants yet. Add one before creating deals.</div>}
        </div>
      </section>}

      {tab === "locations" && <section className="admin-section">
        <div className="section-heading"><h2>All locations</h2><button className="primary-btn small-btn" data-testid="add-location-button" onClick={() => setModal({ type: "location" })}><Plus size={16} /> Add location</button></div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Name</th><th>Status</th><th></th></tr></thead>
            <tbody>{adminLocations.map(l => <tr key={l.id}>
              <td>{l.name}</td>
              <td><button className={`status status-${l.active ? "delivered" : "cancelled"} status-toggle`} onClick={async () => { await api.patch(`/admin/locations/${l.id}`, { name: l.name, active: !l.active }); reload(); }}>{l.active ? "Active" : "Inactive"}</button></td>
              <td><button className="link-btn" data-testid={`edit-location-${l.id}`} onClick={() => setModal({ type: "location", initial: l })}>Edit</button></td>
            </tr>)}</tbody>
          </table>
        </div>
      </section>}
    </main>
    {modal && <AdminModal type={modal.type} initial={modal.initial} locations={adminLocations} restaurants={adminRestaurants} onClose={() => setModal(null)} onDone={() => { setModal(null); reload(); }} />}
  </div>;
}

function ImageUploader({ value, onChange, uploadEndpoint, testid }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState(value && !value.startsWith("/api/files/") ? "url" : "upload");

  const upload = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(""); setUploading(true);
    try {
      const data = new FormData(); data.append("file", file);
      const r = await api.post(uploadEndpoint, data, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Upload failed. Try a smaller image.");
    } finally { setUploading(false); }
  };

  const preview = value ? (value.startsWith("/api/files/") ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value) : "";

  return <div className="uploader">
    <div className="uploader-tabs">
      <button type="button" className={mode === "upload" ? "active" : ""} onClick={() => setMode("upload")}>Upload photo</button>
      <button type="button" className={mode === "url" ? "active" : ""} onClick={() => setMode("url")}>Paste URL</button>
    </div>
    {mode === "upload" ? <>
      <label className="file-drop" data-testid={testid}>
        <input type="file" accept="image/*" onChange={upload} disabled={uploading} />
        <span>{uploading ? "Uploading…" : (value ? "Change photo" : "Tap to pick a photo or snap one")}</span>
        <small>JPG, PNG, WebP or GIF · up to 5 MB</small>
      </label>
    </> : <input type="url" value={value || ""} onChange={e => onChange(e.target.value)} placeholder="https://..." />}
    {preview && <img src={preview} alt="Preview" className="image-preview" />}
    {error && <div className="error-message">{error}</div>}
  </div>;
}

function AdminModal({ type, locations, restaurants, initial, onClose, onDone }) {
  const defaults = type === "location" ? { name: "", active: true }
    : type === "restaurant" ? { name: "", phone: "", whatsapp_number: "", address: "", location_id: locations[0]?.id || "", active: true, email: "", password: "" }
      : { title: "", description: "", image_url: "", restaurant_id: restaurants[0]?.id || "", location_id: locations[0]?.id || "", original_price: "", deal_price: "", discount_amount: 0, delivery_fee: "", expiry_date: "", active: true };
  const [form, setForm] = useState(initial ? { ...defaults, ...initial } : defaults);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const change = e => {
    const { name, type: t, value, checked } = e.target;
    setForm({ ...form, [name]: t === "checkbox" ? checked : value });
  };

  const submit = async e => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      if (type === "location") {
        const payload = { name: form.name, active: form.active };
        initial ? await api.patch(`/admin/locations/${initial.id}`, payload) : await api.post("/admin/locations", payload);
      } else if (type === "restaurant") {
        if (!form.location_id) throw new Error("Pick a location first.");
        const payload = { name: form.name, phone: form.phone || "", whatsapp_number: form.whatsapp_number || "", address: form.address || "", location_id: form.location_id, active: form.active };
        if (form.email) payload.email = form.email;
        if (form.password) payload.password = form.password;
        initial ? await api.patch(`/admin/restaurants/${initial.id}`, payload) : await api.post("/admin/restaurants", payload);
      } else {
        if (!form.restaurant_id) throw new Error("Pick a restaurant first.");
        if (!form.location_id) throw new Error("Pick a location first.");
        const payload = { ...form, original_price: Number(form.original_price), deal_price: Number(form.deal_price), delivery_fee: Number(form.delivery_fee), discount_amount: Number(form.discount_amount || 0) };
        if (payload.deal_price > payload.original_price) throw new Error("Deal price must be less than original.");
        initial ? await api.patch(`/admin/deals/${initial.id}`, payload) : await api.post("/admin/deals", payload);
      }
      onDone();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? d.map(x => x.msg).join(", ") : (typeof d === "string" ? d : err.message || "Something went wrong."));
    } finally { setSaving(false); }
  };

  const action = initial ? "Edit" : "Add";
  const title = `${action} ${type}`;

  return <div className="modal-backdrop" onClick={onClose}>
    <div className="modal" onClick={e => e.stopPropagation()}>
      <div className="modal-head"><h2>{title}</h2><button className="icon-btn" onClick={onClose} data-testid="modal-close"><X size={18} /></button></div>
      <form onSubmit={submit} className="modal-form">
        {type === "location" && <>
          <label>Location name<input name="name" required value={form.name} onChange={change} placeholder="e.g. Model Town" data-testid="location-name-input" /></label>
          <label className="consent"><input name="active" type="checkbox" checked={form.active} onChange={change} /><span>Active (visible to customers)</span></label>
        </>}
        {type === "restaurant" && <>
          <label>Restaurant name<input name="name" required value={form.name} onChange={change} placeholder="e.g. Al Shami Kitchen" data-testid="restaurant-name-input" /></label>
          <label>Phone<input name="phone" value={form.phone} onChange={change} placeholder="03XX XXXXXXX" /></label>
          <label>WhatsApp number for orders<input name="whatsapp_number" value={form.whatsapp_number} onChange={change} placeholder="923001234567" data-testid="restaurant-whatsapp-input" /><small>Country code + number, no + or spaces (e.g. 923001234567).</small></label>
          <label>Address<input name="address" value={form.address} onChange={change} placeholder="Street, area" /></label>
          <label>Location<select name="location_id" required value={form.location_id} onChange={change} data-testid="restaurant-location-select">
            <option value="">Select location</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select></label>
          <div className="form-group-note">
            <strong>Restaurant login (optional)</strong>
            <small>Give the owner an email + password to let them manage their own deals and orders.</small>
          </div>
          <div className="grid-two">
            <label>Owner email<input name="email" type="email" value={form.email} onChange={change} placeholder="owner@example.com" data-testid="restaurant-email-input" /></label>
            <label>Password {initial && <small>leave blank to keep</small>}<input name="password" type="text" value={form.password} onChange={change} placeholder={initial ? "Unchanged" : "Min 6 characters"} data-testid="restaurant-password-input" /></label>
          </div>
          <label className="consent"><input name="active" type="checkbox" checked={form.active} onChange={change} /><span>Active</span></label>
        </>}
        {type === "deal" && <>
          <label>Deal title<input name="title" required value={form.title} onChange={change} placeholder="e.g. Chicken Karahi Combo" data-testid="deal-title-input" /></label>
          <label>Description<textarea name="description" required value={form.description} onChange={change} placeholder="Short, appetising description" /></label>
          <label>Food photo
            <ImageUploader value={form.image_url} onChange={v => setForm(f => ({ ...f, image_url: v }))} uploadEndpoint="/admin/upload" testid="deal-image-uploader" />
          </label>
          <div className="grid-two">
            <label>Restaurant<select name="restaurant_id" required value={form.restaurant_id} onChange={change} data-testid="deal-restaurant-select">
              <option value="">Select restaurant</option>
              {restaurants.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select></label>
            <label>Location<select name="location_id" required value={form.location_id} onChange={change} data-testid="deal-location-select">
              <option value="">Select location</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select></label>
          </div>
          <div className="grid-two">
            <label>Original price (Rs.)<input name="original_price" type="number" min="1" required value={form.original_price} onChange={change} placeholder="1200" data-testid="deal-original-input" /></label>
            <label>Deal price (Rs.)<input name="deal_price" type="number" min="1" required value={form.deal_price} onChange={change} placeholder="799" data-testid="deal-price-input" /></label>
          </div>
          <div className="grid-two">
            <label>Delivery fee (Rs.)<input name="delivery_fee" type="number" min="0" required value={form.delivery_fee} onChange={change} placeholder="100" data-testid="deal-delivery-input" /></label>
            <label>Expiry date <small>optional</small><input name="expiry_date" type="date" value={form.expiry_date} onChange={change} /></label>
          </div>
          <label className="consent"><input name="active" type="checkbox" checked={form.active} onChange={change} /><span>Active (visible to customers)</span></label>
        </>}
        {error && <div className="error-message" data-testid="modal-error">{error}</div>}
        <button type="submit" className="primary-btn full-btn" disabled={saving} data-testid="modal-submit">{saving ? "Saving…" : `Save ${type}`} <ArrowRight size={18} /></button>
      </form>
    </div>
  </div>;
}

function AdminOrderTable({ orders, onUpdate, onDelete }) {
  const statuses = ["NEW", "CONFIRMED", "PREPARING", "OUT_FOR_DELIVERY", "DELIVERED", "CANCELLED"];
  const fmt = iso => {
    try { const d = new Date(iso); return { date: d.toLocaleDateString("en-GB"), time: d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) }; }
    catch { return { date: "", time: "" }; }
  };
  return <div className="table-wrap">
    <table className="orders-table">
      <thead><tr><th>Date</th><th>Time</th><th>Order No.</th><th>Customer</th><th>Phone</th><th>Restaurant</th><th>Deal</th><th>Qty</th><th>Total</th><th>Address</th><th>Status</th><th></th></tr></thead>
      <tbody>{orders.map(o => {
        const t = fmt(o.created_at);
        return <tr key={o.id} data-testid={`order-row-${o.order_number}`}>
          <td>{t.date}</td>
          <td>{t.time}</td>
          <td><strong>{o.order_number}</strong></td>
          <td>{o.customer_name}</td>
          <td>{o.phone}</td>
          <td>{o.restaurant_name}<small>{o.location_name}</small></td>
          <td>{o.deal_title}</td>
          <td>{o.quantity}</td>
          <td><strong>{money(o.total)}</strong></td>
          <td className="addr-cell" title={o.delivery_address}>{o.delivery_address}</td>
          <td><select data-testid={`status-select-${o.order_number}`} value={o.status} onChange={e => onUpdate(o.id, e.target.value)} className={`status status-${o.status.toLowerCase()}`}>{statuses.map(s => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}</select></td>
          <td><button className="link-btn danger" data-testid={`delete-order-${o.order_number}`} onClick={() => onDelete(o.id, o.order_number)}>Delete</button></td>
        </tr>;
      })}</tbody>
    </table>
    {!orders.length && <div className="empty-state">No orders yet.</div>}
  </div>;
}

function OrderTable({ orders, onUpdate }) {
  const statuses = ["NEW", "CONFIRMED", "PREPARING", "OUT_FOR_DELIVERY", "DELIVERED", "CANCELLED"];
  return <div className="table-wrap">
    <table>
      <thead><tr><th>Order</th><th>Customer</th><th>Deal</th><th>Total</th><th>Status</th></tr></thead>
      <tbody>{orders.map(o => <tr key={o.id} data-testid={`order-row-${o.order_number}`}>
        <td><strong>{o.order_number}</strong><small>{new Date(o.created_at).toLocaleDateString()}</small></td>
        <td>{o.customer_name}<small>{o.phone}</small></td>
        <td>{o.deal_title}<small>{o.location_name}</small></td>
        <td><strong>{money(o.total)}</strong></td>
        <td>{onUpdate ? <select data-testid={`status-select-${o.order_number}`} value={o.status} onChange={e => onUpdate(o.id, e.target.value)} className={`status status-${o.status.toLowerCase()}`}>{statuses.map(s => <option key={s} value={s}>{s.replaceAll("_", " ")}</option>)}</select>
          : <span className={`status status-${o.status.toLowerCase()}`}>{o.status.replaceAll("_", " ")}</span>}</td>
      </tr>)}</tbody>
    </table>
    {!orders.length && <div className="empty-state">No orders yet.</div>}
  </div>;
}

function RestaurantPortal() {
  const [me, setMe] = useState(null); // null = checking, false = login, {id,...} = in
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [deals, setDeals] = useState([]);
  const [locations, setLocations] = useState([]);
  const [modalOpen, setModalOpen] = useState(null); // null | "new" | deal

  useEffect(() => {
    api.get("/auth/me").then(r => { r.data.role === "restaurant" ? setMe(r.data) : setMe(false); }).catch(() => setMe(false));
    api.get("/locations").then(r => setLocations(r.data));
  }, []);

  const reload = async () => {
    const [s, o, d] = await Promise.all([api.get("/restaurant/stats"), api.get("/restaurant/orders"), api.get("/restaurant/deals")]);
    setStats(s.data); setOrders(o.data); setDeals(d.data);
  };
  useEffect(() => { if (me && me.restaurant_id) reload(); }, [me]);

  const login = async e => {
    e.preventDefault(); setError("");
    try { const r = await api.post("/auth/login", { email, password }); if (r.data.role !== "restaurant") throw new Error("Please use the /admin login for admin accounts."); setMe(r.data); }
    catch (err) { setError(err.response?.data?.detail || err.message || "Invalid email or password"); }
  };

  const updateStatus = async (orderId, status) => { await api.patch(`/restaurant/orders/${orderId}`, { status }); reload(); };
  const toggleDeal = async (d) => { await api.patch(`/restaurant/deals/${d.id}`, { ...d, active: !d.active }); reload(); };
  const deleteDeal = async (id) => { if (window.confirm("Delete this deal? Customers won't see it anymore.")) { await api.delete(`/restaurant/deals/${id}`); reload(); } };

  if (me === null) return <div className="loading">Loading restaurant portal…</div>;

  if (!me) return <div className="admin-login">
    <Brand />
    <form onSubmit={login}>
      <span className="eyebrow">RESTAURANT PORTAL</span>
      <h1>Partner <em>sign in.</em></h1>
      <p className="muted">Manage your deals and track incoming orders.</p>
      <label>Email<input data-testid="restaurant-login-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="owner@restaurant.com" /></label>
      <label>Password<input data-testid="restaurant-login-password" type="password" required value={password} onChange={e => setPassword(e.target.value)} /></label>
      {error && <div className="error-message" data-testid="restaurant-login-error">{error}</div>}
      <button className="primary-btn full-btn" data-testid="restaurant-login-button">Sign in <ArrowRight size={18} /></button>
    </form>
    <Link to="/">← Back to FOODISTAN</Link>
  </div>;

  return <div className="admin-layout">
    <aside className="admin-sidebar">
      <Brand />
      <p>{me.restaurant_name?.toUpperCase()}</p>
      {[["overview", "Overview"], ["orders", "Orders"], ["deals", "My deals"]].map(([id, label]) =>
        <button className={tab === id ? "active" : ""} data-testid={`restaurant-tab-${id}`} key={id} onClick={() => setTab(id)}><LayoutDashboard size={17} />{label}</button>)}
      <button className="logout" data-testid="restaurant-logout-button" onClick={async () => { await api.post("/auth/logout"); setMe(false); }}><LogOut size={17} />Sign out</button>
    </aside>
    <main className="admin-main">
      <div className="admin-top">
        <div>
          <span className="eyebrow">RESTAURANT · {me.restaurant_name}</span>
          <h1>{tab === "overview" ? "Dashboard" : tab[0].toUpperCase() + tab.slice(1)}</h1>
        </div>
        <span className="admin-chip">Partner <ShieldCheck size={15} /></span>
      </div>

      {tab === "overview" && <>
        <div className="stats-grid">
          {[["orders", "Total orders"], ["new_orders", "New orders"], ["delivered", "Delivered"], ["deals", "Active deals"]].map(([k, l]) =>
            <div className="stat-card" key={k}><span>{l}</span><strong>{stats?.[k] ?? "—"}</strong></div>)}
        </div>
        <section className="admin-section">
          <div className="section-heading"><h2>Recent orders</h2><button onClick={() => setTab("orders")}>View all <ArrowRight size={16} /></button></div>
          <OrderTable orders={orders.slice(0, 5)} onUpdate={updateStatus} />
        </section>
      </>}

      {tab === "orders" && <section className="admin-section"><OrderTable orders={orders} onUpdate={updateStatus} /></section>}

      {tab === "deals" && <section className="admin-section">
        <div className="section-heading"><h2>My deals</h2><button className="primary-btn small-btn" data-testid="restaurant-add-deal" onClick={() => setModalOpen("new")}><Plus size={16} /> Add deal</button></div>
        <div className="table-wrap">
          <table>
            <thead><tr><th>Title</th><th>Deal price</th><th>Original</th><th>Delivery</th><th>Status</th><th></th></tr></thead>
            <tbody>{deals.map(d => <tr key={d.id}>
              <td>{d.title}</td><td>{money(d.deal_price)}</td><td>{money(d.original_price)}</td><td>{money(d.delivery_fee)}</td>
              <td><button className={`status status-${d.active ? "delivered" : "cancelled"} status-toggle`} onClick={() => toggleDeal(d)}>{d.active ? "Active" : "Inactive"}</button></td>
              <td><button className="link-btn" onClick={() => setModalOpen(d)}>Edit</button> · <button className="link-btn danger" onClick={() => deleteDeal(d.id)}>Delete</button></td>
            </tr>)}</tbody>
          </table>
          {!deals.length && <div className="empty-state">No deals yet. Click "Add deal" to create your first one.</div>}
        </div>
      </section>}
    </main>
    {modalOpen && <RestaurantDealModal restaurant={me} locations={locations} initial={modalOpen === "new" ? null : modalOpen} onClose={() => setModalOpen(null)} onDone={() => { setModalOpen(null); reload(); }} />}
  </div>;
}

function RestaurantDealModal({ restaurant, locations, initial, onClose, onDone }) {
  const defaults = { title: "", description: "", image_url: "", restaurant_id: restaurant.restaurant_id, location_id: restaurant.location_id || locations[0]?.id || "", original_price: "", deal_price: "", discount_amount: 0, delivery_fee: "", expiry_date: "", active: true };
  const [form, setForm] = useState(initial ? { ...defaults, ...initial } : defaults);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const change = e => { const { name, type, value, checked } = e.target; setForm({ ...form, [name]: type === "checkbox" ? checked : value }); };
  const submit = async e => {
    e.preventDefault(); setError(""); setSaving(true);
    try {
      const payload = { ...form, restaurant_id: restaurant.restaurant_id, original_price: Number(form.original_price), deal_price: Number(form.deal_price), delivery_fee: Number(form.delivery_fee), discount_amount: Number(form.discount_amount || 0) };
      if (payload.deal_price > payload.original_price) throw new Error("Deal price must be less than original.");
      initial ? await api.patch(`/restaurant/deals/${initial.id}`, payload) : await api.post("/restaurant/deals", payload);
      onDone();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? d.map(x => x.msg).join(", ") : (typeof d === "string" ? d : err.message || "Something went wrong."));
    } finally { setSaving(false); }
  };
  return <div className="modal-backdrop" onClick={onClose}>
    <div className="modal" onClick={e => e.stopPropagation()}>
      <div className="modal-head"><h2>{initial ? "Edit deal" : "Add deal"}</h2><button className="icon-btn" onClick={onClose}><X size={18} /></button></div>
      <form onSubmit={submit} className="modal-form">
        <label>Deal title<input name="title" required value={form.title} onChange={change} placeholder="e.g. Chicken Karahi Combo" /></label>
        <label>Description<textarea name="description" required value={form.description} onChange={change} /></label>
        <label>Food photo<ImageUploader value={form.image_url} onChange={v => setForm(f => ({ ...f, image_url: v }))} uploadEndpoint="/restaurant/upload" /></label>
        <label>Location<select name="location_id" required value={form.location_id} onChange={change}>
          <option value="">Select location</option>
          {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select></label>
        <div className="grid-two">
          <label>Original price (Rs.)<input name="original_price" type="number" min="1" required value={form.original_price} onChange={change} placeholder="1200" /></label>
          <label>Deal price (Rs.)<input name="deal_price" type="number" min="1" required value={form.deal_price} onChange={change} placeholder="799" /></label>
        </div>
        <div className="grid-two">
          <label>Delivery fee (Rs.)<input name="delivery_fee" type="number" min="0" required value={form.delivery_fee} onChange={change} placeholder="100" /></label>
          <label>Expiry date <small>optional</small><input name="expiry_date" type="date" value={form.expiry_date} onChange={change} /></label>
        </div>
        <label className="consent"><input name="active" type="checkbox" checked={form.active} onChange={change} /><span>Active (visible to customers)</span></label>
        {error && <div className="error-message">{error}</div>}
        <button type="submit" className="primary-btn full-btn" disabled={saving}>{saving ? "Saving…" : (initial ? "Save changes" : "Add deal")} <ArrowRight size={18} /></button>
      </form>
    </div>
  </div>;
}

export default function App() {
  return <BrowserRouter>
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/locations" element={<Locations />} />
      <Route path="/deals" element={<Deals />} />
      <Route path="/deal/:id" element={<Detail />} />
      <Route path="/order/:id" element={<Order />} />
      <Route path="/confirmation/:id" element={<Confirmation />} />
      <Route path="/admin" element={<Admin />} />
      <Route path="/restaurant" element={<RestaurantPortal />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  </BrowserRouter>;
}
